import styled from '@emotion/styled';

const profilePrimary = 'var(--profile-primary, #6025D8)';
const profilePrimaryDark = 'var(--profile-primary-dark, #4c1dab)';
const profilePrimaryMuted = 'var(--profile-primary-muted, rgba(96, 37, 216, 0.1))';
const profileOnPrimary = 'var(--profile-on-primary, #ffffff)';
const profileSurfaceRaised = 'var(--profile-surface-raised, #ffffff)';

export const OverviewContainer = styled.div`
    display: flex;
    gap: 16px;
    padding: 16px 18px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: ${profileSurfaceRaised};
    box-sizing: border-box;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;

    @media (max-width: 768px) {
        flex: 0 1 auto;
        flex-direction: column;
        margin: 12px 16px;
        padding: 16px;
        gap: 16px;
        height: auto;
        min-height: unset;
        overflow: visible;
    }

    @media (max-width: 480px) {
        margin: 12px;
        padding: 14px;
    }
`;

export const SideBarSection = styled.section`
    flex: 0 0 clamp(188px, 24%, 240px);
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-right: 8px;
    min-height: 0;
    overflow-y: auto;
    border-right: 1px solid #e2e8f0;

    @media (max-width: 768px) {
        display: none;
    }
`;

export const MobileNav = styled.nav`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
    }
`;

export const NavItemWrap = styled.span<{ $chip?: boolean }>`
    display: ${({ $chip }) => ($chip ? 'inline-flex' : 'block')};
    width: ${({ $chip }) => ($chip ? 'auto' : '100%')};
`;

export const ChipRail = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        height: 4px;
    }
`;

export const SectionChip = styled.button<{ $isActive?: boolean; $isDisabled?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    padding: 6px 12px;
    border: 1px solid ${({ $isActive }) => ($isActive ? profilePrimary : '#e2e8f0')};
    border-radius: 999px;
    background: ${({ $isActive, $isDisabled }) =>
        $isDisabled ? '#f8fafc' : $isActive ? profilePrimaryMuted : '#ffffff'};
    color: ${({ $isActive, $isDisabled }) =>
        $isDisabled ? '#94a3b8' : $isActive ? profilePrimary : '#0f172a'};
    font: inherit;
    font-size: 13px;
    font-weight: ${({ $isActive }) => ($isActive ? 600 : 500)};
    line-height: 1.2;
    white-space: nowrap;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $isDisabled }) => ($isDisabled ? 0.7 : 1)};

    &:hover:not(:disabled) {
        border-color: ${profilePrimary};
        color: ${profilePrimary};
    }

    &:focus-visible {
        outline: 2px solid ${profilePrimary};
        outline-offset: 2px;
    }
`;

export const SideBarProgress = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0 4px 8px;
    margin-bottom: 2px;
    flex-shrink: 0;
`;

export const SideBarProgressLabel = styled.span`
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
`;

export const SideBarProgressTrack = styled.div`
    height: 4px;
    overflow: hidden;
    border-radius: 999px;
    background: #e2e8f0;
`;

export const SideBarProgressFill = styled.div<{ $percent: number }>`
    width: ${({ $percent }) => `${Math.max(0, Math.min(100, $percent))}%`};
    height: 100%;
    border-radius: 999px;
    background: ${profilePrimary};
`;

export const UpdateAssessmentDateButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    align-self: flex-start;
    margin-top: 3px;
    padding: 5px 8px;
    border: 1px solid ${profilePrimary};
    border-radius: 7px;
    background: ${profilePrimaryMuted};
    color: ${profilePrimary};
    font: inherit;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${profilePrimary};
        color: ${profileOnPrimary};
    }

    &:focus-visible {
        outline: 2px solid ${profilePrimary};
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.55;
        cursor: wait;
    }
`;

export const SideBarGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding-bottom: 6px;

    &:last-of-type {
        padding-bottom: 0;
    }
`;

export const SideBarGroupLabel = styled.span`
    padding: 6px 10px 2px;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

export const SideBarItem = styled.button<{ $isActive?: boolean; $isDisabled?: boolean }>`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 8px 6px 12px;
    border: none;
    border-radius: 8px;
    background: ${({ $isActive, $isDisabled }) =>
        $isDisabled ? 'transparent' : $isActive ? profilePrimaryMuted : 'transparent'};
    color: ${({ $isActive, $isDisabled }) =>
        $isDisabled ? '#94a3b8' : $isActive ? profilePrimary : '#0f172a'};
    font: inherit;
    font-size: 13px;
    font-weight: ${({ $isActive }) => ($isActive ? 600 : 500)};
    line-height: 1.25;
    text-align: left;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $isDisabled }) => ($isDisabled ? 0.7 : 1)};

    &::before {
        content: '';
        position: absolute;
        top: 6px;
        bottom: 6px;
        left: 0;
        width: 3px;
        border-radius: 999px;
        background: ${({ $isActive, $isDisabled }) =>
            $isActive && !$isDisabled ? profilePrimary : 'transparent'};
    }

    &:hover:not(:disabled) {
        background: ${profilePrimaryMuted};
        color: ${profilePrimary};
    }

    &:focus-visible {
        outline: 2px solid ${profilePrimary};
        outline-offset: 2px;
    }
`;

export const SideBarItemText = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const SideBarDot = styled.span<{ $done?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #ffffff;
    line-height: 0;

    ${({ $done }) =>
        $done
            ? `
        width: 16px;
        height: 16px;
        min-width: 16px;
        min-height: 16px;
        border-radius: 999px;
        background: #10b981;
    `
            : `
        width: 0;
        height: 0;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
    `}
`;

export const InfoSection = styled.section<{ mobile: boolean }>`
    width: ${({ mobile }) => (mobile ? '100%' : 'auto')};
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-height: 0;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    padding-right: 4px;

    @media (max-width: 768px) {
        flex: 0 1 auto;
        overflow: visible;
    }
`;

export const InfoSectionBody = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 2px;

    @media (max-width: 768px) {
        flex: 0 1 auto;
        overflow: visible;
    }
`;

export const InfoSectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-bottom: 10px;
    margin-bottom: 4px;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
`;

export const InfoSectionHeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;

    @media (max-width: 640px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

export const InfoSectionHeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

export const InfoSectionHeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

export const EditProfileButton = styled.button<{ $quiet?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid ${({ $quiet }) => ($quiet ? '#e2e8f0' : 'transparent')};
    border-radius: 8px;
    background: ${({ $quiet }) => ($quiet ? '#ffffff' : profilePrimary)};
    color: ${({ $quiet }) => ($quiet ? '#334155' : profileOnPrimary)};
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${({ $quiet }) => ($quiet ? '#f8fafc' : profilePrimaryDark)};
    }

    &:focus-visible {
        outline: 2px solid ${profilePrimary};
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

export const EditPane = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 18px;
    margin-top: 0;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: ${profileSurfaceRaised};
    min-height: 0;

    .form-container,
    .sales-form-container {
        width: 100%;
        margin: 0;
    }

    .form-card {
        padding: 0;
        margin: 0;
        background: transparent;
        border: none;
        box-shadow: none;
        max-height: none;
        overflow: visible;
    }

    [data-form-section-title],
    [data-form-footer-title] {
        display: none;
    }

    [data-form-language-title],
    [data-form-subtitle] {
        font-size: 14px;
        font-weight: 600;
        line-height: 1.3;
    }

    [data-form-question] {
        font-size: 14px;
        font-weight: 400;
        line-height: 1.45;
        color: #475569;
    }

    [data-form-toggle-label] {
        font-size: 14px;
        font-weight: 600;
        line-height: 1.45;
        color: #475569;
        letter-spacing: 0;
    }

    [data-form-toggle-label] + div button {
        font-size: 13px;
    }

    p:has(+ .MuiSlider-root) {
        margin: 0 0 4px;
        color: #475569;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.3;
    }

    .MuiSlider-markLabel {
        font-size: 13px !important;
        line-height: 1.2;
    }

    .MuiSlider-valueLabel {
        font-size: 13px !important;
        font-weight: 600 !important;
        padding: 2px 6px !important;
        line-height: 1.2;
    }

    [data-form-add-entry] {
        padding: 4px 0 8px;
        justify-content: flex-start;
    }

    [data-form-add-entry] button {
        margin-left: 0;
        font-size: 13px;
        max-height: 36px;
        padding: 6px 12px;
    }

    [data-form-grid='container'] {
        grid-row-gap: 20px;
    }

    [data-form-grid='section'] {
        margin: 8px 0 16px;
        grid-row-gap: 20px;
        grid-column-gap: 14px;
    }

    [data-form-entry='card'] {
        gap: 16px;
        margin: 8px 0 16px;
    }

    [data-form-grid='section']:has(> button[style*='56px']) {
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
    }

    .MuiFormControl-root {
        min-height: 0;
    }

    .MuiOutlinedInput-root {
        min-height: 0;
        height: auto !important;
        font-size: 14px;
    }

    .MuiOutlinedInput-input,
    .MuiSelect-select,
    .MuiInputBase-input {
        box-sizing: border-box;
        padding: 8px 12px !important;
        font-size: 14px !important;
        line-height: 1.4 !important;
        height: auto !important;
        min-height: 0 !important;
    }

    .MuiSelect-select {
        padding-right: 32px !important;
        display: flex;
        align-items: center;
    }

    .MuiInputBase-multiline {
        padding: 8px 12px !important;
    }

    .MuiInputBase-inputMultiline {
        padding: 0 !important;
    }

    .MuiAutocomplete-inputRoot {
        padding: 2px 8px !important;
    }

    .MuiAutocomplete-input {
        padding: 6px 4px !important;
    }

    .MuiInputLabel-outlined {
        font-size: 14px;
    }

    .MuiFormControl-root:not(.MuiTelInput-TextField)
        .MuiInputLabel-outlined:not(.MuiInputLabel-shrink) {
        transform: translate(12px, 8px) scale(1) !important;
    }

    .MuiFormControl-root:not(.MuiTelInput-TextField) .MuiInputLabel-outlined.MuiInputLabel-shrink,
    .MuiFormControl-root:not(.MuiTelInput-TextField) .MuiInputLabel-outlined.Mui-focused {
        transform: translate(12px, -9px) scale(0.79) !important;
    }

    .MuiTelInput-TextField .MuiInputLabel-root {
        transform: translate(87px, 8px) scale(1) !important;
    }

    .MuiTelInput-TextField.Mui-focused .MuiInputLabel-root,
    .MuiTelInput-TextField .MuiInputLabel-root.Mui-focused,
    .MuiTelInput-TextField .MuiInputLabel-root.MuiFormLabel-filled {
        transform: translate(14px, -9px) scale(0.79) !important;
    }

    .MuiFormHelperText-root {
        margin-top: 4px;
        font-size: 13px;
    }

    .MuiPickersOutlinedInput-root,
    .MuiPickersInputBase-root {
        min-height: 0;
        height: auto !important;
        padding: 0 8px 0 12px !important;
        font-size: 14px;
    }

    .MuiPickersInputBase-sectionsContainer,
    .MuiPickersOutlinedInput-sectionsContainer,
    .MuiPickersSectionList-root {
        padding: 8px 0 !important;
        font-size: 14px !important;
        line-height: 1.4 !important;
    }

    .MuiPickersSectionList-sectionContent,
    .MuiPickersInputBase-sectionContent {
        font-size: 14px !important;
        line-height: 1.4 !important;
    }

    .MuiPickersInputAdornment-root .MuiIconButton-root,
    .MuiPickersOutlinedInput-root .MuiIconButton-root {
        width: 28px;
        height: 28px;
        padding: 4px;
    }

    .MuiPickersInputAdornment-root svg,
    .MuiPickersOutlinedInput-root .MuiIconButton-root svg {
        width: 18px;
        height: 18px;
    }

    button[style*='56px'] {
        height: 38px !important;
        min-height: 38px;
        width: auto !important;
        min-width: 0;
        max-width: 120px;
        align-self: center;
        justify-self: end;
        font-size: 12px;
        padding: 0 8px;
        gap: 4px;
    }

    button[style*='56px'] svg {
        width: 16px !important;
        height: 16px !important;
    }

    .profile-edit-actions {
        position: sticky;
        bottom: 0;
        z-index: 2;
        margin: 4px -20px -20px;
        padding: 16px 20px 20px;
        background: ${profileSurfaceRaised};
        border-top: 1px solid #e2e8f0;
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
    }

    @media (max-width: 640px) {
        padding: 14px;
        border-radius: 10px;

        .profile-edit-actions {
            margin: 4px -14px -14px;
            padding: 12px 14px 14px;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
        }
    }
`;

export const InfoSectionEyebrow = styled.span`
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

export const InfoSectionTitle = styled.h2`
    margin: 0;
    color: #0f172a;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.2;
`;

export const InfoSectionSubtitle = styled.p`
    margin: 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.4;
`;

export const InfoItem = styled.div`
    display: grid;
    grid-template-columns: minmax(200px, 280px) minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    padding: 8px 4px;
    border-bottom: 1px solid #e2e8f0;

    &:last-of-type {
        border-bottom: none;
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 4px;
        padding: 10px 4px;
    }
`;

export const Label = styled.div<{ mobile?: boolean }>`
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: ${({ mobile }) => (mobile ? 'auto' : 'hidden')};
    text-overflow: ${({ mobile }) => (mobile ? 'unset' : 'ellipsis')};
`;

export const Info = styled.div<{ mobile: boolean }>`
    color: #0f172a;
    font-size: 15px;
    font-weight: 500;
    white-space: nowrap;
    overflow: ${({ mobile }) => (mobile ? 'auto' : 'hidden')};
    text-overflow: ${({ mobile }) => (mobile ? 'unset' : 'ellipsis')};
`;

export const Title = styled.div`
    color: #0f172a;
    font-size: 18px;
    font-weight: 700;
    padding: 16px 0 6px;
`;
